import React, { useState, useContext, useEffect } from 'react';
import { UserSettingsContext } from '../../../setup/routes-manager/RequireAuth';
import { Box, List, ListItemButton, ListItemText, ListItemIcon, Typography, Grid, Paper, IconButton, Button, useMediaQuery } from '@mui/material';

// Import images
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import FlashOnOutlinedIcon from '@mui/icons-material/FlashOnOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CandlestickChartOutlinedIcon from '@mui/icons-material/CandlestickChartOutlined';
import WaterfallChartOutlinedIcon from '@mui/icons-material/WaterfallChartOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import FiberNewIcon from '@mui/icons-material/FolderSpecial';
import PremiumAccessIcon from '@mui/icons-material/WorkspacePremium';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { MANUAL_SECTIONS } from '../../../common/utility/constant';



// Icons for each main section (index aligned)
const SECTION_ICONS = [
  <HomeOutlinedIcon sx={{ color: '#fff' }} />, // Start Here
  <MenuBookOutlinedIcon sx={{ color: '#fff' }} />, // How to Use Vtrender Charts
  <FlashOnOutlinedIcon sx={{ color: '#fff' }} />, // Quick Start
  <AccountTreeOutlinedIcon sx={{ color: '#fff' }} />, // OrderFlow
  <ShowChartIcon sx={{ color: '#fff' }} />, // Explore Marketprofile
  <CandlestickChartOutlinedIcon sx={{ color: '#fff' }} />, // Smart Candlesticks
  <WaterfallChartOutlinedIcon sx={{ color: '#fff' }} />, // Explore Options
  <PremiumAccessIcon sx={{ color: '#fff' }} />, // Premium Access
  <FiberNewIcon sx={{ color: '#fff' }} />, // Free Access
  <BookmarksOutlinedIcon sx={{ color: '#fff' }} />, // Bookmarks and Layout
];

// Placeholder card content for right pane
const ManualAccordion = ({ title, description }) => {
  return (
    <Accordion 
      sx={{ 
        backgroundColor: '#183669ff', 
        color: '#fff', 
        mb: 2.5, 
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', 
        borderRadius: '8px !important',
        overflow: 'hidden',
        border: '1px solid rgba(63, 105, 248, 0.15)',
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: '0 0 20px 0',
          borderLeft: '4px solid #3F69F8',
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: '#1132a0ff', fontSize: '1.4rem' }} />}
        aria-controls="panel1a-content"
        id="panel1a-header"
        sx={{
          minHeight: '48px',
          '&.Mui-expanded': {
            backgroundColor: 'rgba(17, 95, 98, 0.51)',
            minHeight: '48px',
          },
          '& .MuiAccordionSummary-content': {
            margin: '2px 0',
            '&.Mui-expanded': {
              margin: '2px 0',
            }
          }
        }}
      >
        <div 
          dangerouslySetInnerHTML={{ __html: title }} 
          style={{ 
            fontWeight: 500,
            fontSize: '1.05rem',
          }}
        />
      </AccordionSummary>
      <AccordionDetails sx={{
        padding: '16px 24px 24px',
        borderTop: '1px solid rgba(63, 105, 248, 0.1)',
        '& h2': {
          fontSize: '1.2rem',
          fontWeight: 500,
          marginBottom: '16px',
          color: '#fff',
        },

        '& p': {
          marginBottom: '16px',
          lineHeight: 1.6,
          fontSize: '0.95rem',
        },
        '& ul, & ol': {
          paddingLeft: '24px',
          marginBottom: '16px',
          marginTop: '8px',
        },
        '& li': {
          marginBottom: '10px',
          lineHeight: 1.5,
          fontSize: '0.95rem',
          paddingLeft: '4px',
        },
        '& li ul, & li ol': {
          marginTop: '10px',
          marginBottom: '10px',
        },
        '& li li': {
          marginBottom: '6px',
        },
        '& h3': {
          marginTop: '24px',
          fontSize: '1rem',
          fontWeight: 500,
          marginBottom: '12px',
          color: '#e0e6f5',
        },
        '& h4': {
          marginTop: '20px',
          marginLeft: '12px',
          paddingLeft: '8px',
          borderLeft: '2px solid rgba(63, 105, 248, 0.5)',
        },
        '& hr + h4': {
          marginTop: '16px',
        },
        '& h4 + p, & h4 + ul, & h4 + ol': {
          marginLeft: '12px',
          paddingLeft: '8px',
        },
        '& strong': {
          color: '#4deeea',
          fontWeight: 600,
        },
        '& em': {
          color: '#e0e6f5',
          fontStyle: 'italic',
        },
        '& .tip': {
          backgroundColor: 'rgba(63, 105, 248, 0.1)',
          borderLeft: '4px solid #3F69F8',
          padding: '12px 16px',
          borderRadius: '4px',
          marginTop: '16px',
          marginBottom: '16px',
          '& strong': {
            color: '#ff9d4d',
            fontWeight: 600,
          },
        },
        '& .signal': {
          backgroundColor: 'rgba(0, 200, 83, 0.1)',
          borderLeft: '4px solid #00c853',
          padding: '12px 16px',
          borderRadius: '4px',
          marginBottom: '8px',
        },
        '& img': {
          maxWidth: '100%',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
          margin: '16px 0',
          display: 'block',
          height: 'auto',
          '@media (min-width: 600px)': {
            maxWidth: '80%',
            marginLeft: 'auto',
            marginRight: 'auto',
          },
        },
        '& table': {
          width: '100%',
          borderCollapse: 'collapse',
          marginBottom: '20px',
          backgroundColor: 'rgba(19, 45, 90, 0.5)',
          border: '2px solid #3F69F8',
          overflow: 'hidden',
        },
        '& th': {
          backgroundColor: 'rgba(63, 105, 248, 0.2)',
          color: '#4deeea',
          padding: '12px 8px',
          textAlign: 'left',
          fontWeight: 600,
          borderBottom: '2px solid #3F69F8',
          borderRight: '2px solid #3F69F8',
          fontSize: '0.9rem',
        },
        '& td': {
          padding: '10px 8px',
          borderBottom: '2px solid #3F69F8',
          borderRight: '2px solid #3F69F8',
          fontSize: '0.9rem',
          color: '#e0e6f5',
        },
        '& tr': {
          borderBottom: '2px solid #3F69F8',
        },
        '& tr:last-child': {
          borderBottom: 'none',
        },
        '& th:last-child, & td:last-child': {
          borderRight: '2px solid #3F69F8',
        },
      }}>
        <div dangerouslySetInnerHTML={{ __html: description }} />
      </AccordionDetails>
    </Accordion>
  );
};

const UserManualNew = () => {
  const { showMPSection, setShowMPSection } = useContext(UserSettingsContext);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const isNarrow = useMediaQuery('(max-width:1024px)');
  const isMobile = useMediaQuery('(max-width:600px)');
  const isCompactHeader = useMediaQuery('(max-width:1200px)');
  const isDesktop = useMediaQuery('(min-width:1025px)');
  // const learningPathPdf = '/assets/Vtrender%20Learning%20Pathway%20for%20charts%20with%20links.pdf';
  const learningPathPdf = '/assets/Vtrender_9_Pillar_Learning_Pathway_Download_v2.pdf';

  useEffect(() => {
    if (showMPSection >= 0) {
      setSelectedIndex(showMPSection);
      setShowMPSection(-1);
    }
  }, [showMPSection, setShowMPSection]);

  React.useEffect(() => {
    if (isNarrow) {
      setDrawerOpen(false);
      setCollapsed(false);
    } else {
      setDrawerOpen(true);
    }
  }, [isNarrow]);

  const handleListItemClick = (index) => {
    setSelectedIndex(index);
    if (isNarrow) {
      setDrawerOpen(false);
    }
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden', position: 'relative' }}>
      {isNarrow && drawerOpen && (
        <Box
          onClick={toggleDrawer}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1100,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
          }}
        />
      )}
      {/* Left Drawer with Menu */}
      <Box
        sx={{
          width: drawerOpen
            ? isNarrow
              ? 'min(300px, 88vw)'
              : collapsed && isDesktop
                ? '70px'
                : '300px'
            : '0px',
          flexShrink: 0,
          transition: 'width 0.2s',
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: isNarrow ? 'absolute' : 'relative',
          zIndex: isNarrow ? 1200 : 'auto',
          backgroundColor: '#0d2147',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: isNarrow && drawerOpen ? '4px 0 24px rgba(0,0,0,0.35)' : 'none',
        }}
      >
        <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          {!collapsed && (
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              User Manual
            </Typography>
          )}
          {isNarrow ? (
            <IconButton onClick={toggleDrawer} sx={{ color: '#fff' }}>
              <ChevronLeftIcon />
            </IconButton>
          ) : (
            <IconButton onClick={toggleCollapse} sx={{ color: '#fff' }}>
              {collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
            </IconButton>
          )}
        </Box>
        <List
          sx={{
            width: '100%',
            pt: 0,
            pb: 2,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(63, 105, 248, 0.5)', borderRadius: 3 },
          }}
        >
          {MANUAL_SECTIONS.map((section, index) => (
            <React.Fragment key={`section-${index}`}>
              <ListItemButton
                id={section.title === 'Explore Marketprofile' ? 'manual-section-marketprofile' : undefined}
                selected={selectedIndex === index}
                onClick={() => handleListItemClick(index)}
                sx={{
                  py: { xs: 1.1, sm: 1.25 },
                  px: { xs: 1.25, sm: 1.75 },
                  minHeight: 44,
                  alignItems: 'center',
                  backgroundColor: selectedIndex === index ? '#3F69F8' : 'transparent',
                  '&:hover': {
                    backgroundColor: selectedIndex === index ? '#3F69F8' : 'rgba(63, 105, 248, 0.1)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: collapsed ? 'auto' : 36, mr: collapsed ? 0 : 1.5, justifyContent: collapsed ? 'center' : 'center', flexShrink: 0 }}>
                  {SECTION_ICONS[index]}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={section.title}
                    sx={{ m: 0, minWidth: 0 }}
                    primaryTypographyProps={{
                      noWrap: true,
                      sx: {
                        color: '#fff',
                        fontWeight: selectedIndex === index ? 'bold' : 'normal',
                        fontSize: { xs: '0.8rem', sm: '0.86rem', md: '0.9rem' },
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          height: '100%',
          overflow: 'auto',
          backgroundColor: '#132d5a',
          position: 'relative',
          p: { xs: 2, sm: 2.5, md: 3 },
          px: { xs: 1.5, sm: 2, md: 2.5 },
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#3F69F8', borderRadius: 4 },
        }}
      >
        {/* Mobile Menu Toggle Button */}
        {isNarrow && !drawerOpen && (
          <IconButton
            onClick={toggleDrawer}
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              zIndex: 1100,
              color: '#fff',
              backgroundColor: '#3F69F8',
              '&:hover': { backgroundColor: '#3357d6' },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Section Title and Header */}
        <Paper 
          elevation={0}
          sx={{
            mb: 4, 
            p: 3,
            backgroundColor: 'rgba(110, 139, 233, 0.08)',
            borderRadius: '12px',
            borderLeft: '4px solid #3F69F8',
            mt: isNarrow && !drawerOpen ? 5 : 0,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
              <Typography
                variant="h5"
                sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: { xs: '1.5rem', md: '1.8rem' },
                  mb: 1,
                }}
              >
                {MANUAL_SECTIONS[selectedIndex].title}
              </Typography>
              <Typography 
                variant="body1"
                sx={{
                  color: '#e0e6f5',
                  fontSize: '0.95rem',
                }}
              >
                Explore everything you need to know about {MANUAL_SECTIONS[selectedIndex].title.toLowerCase()}
              </Typography>
            </Box>

            <Button
              component="a"
              href={learningPathPdf}
              download
              variant="outlined"
              startIcon={<DownloadOutlinedIcon sx={{ fontSize: isCompactHeader ? 14 : 16 }} />}
              sx={{
                color: '#dce7ff',
                borderColor: 'rgba(122, 156, 255, 0.55)',
                backgroundColor: 'rgba(63, 105, 248, 0.08)',
                whiteSpace: 'nowrap',
                fontSize: isCompactHeader ? '0.74rem' : '0.82rem',
                px: isCompactHeader ? 1.2 : 1.8,
                py: isCompactHeader ? 0.5 : 0.75,
                maxWidth: { xs: '100%', sm: 'unset' },
                alignSelf: { xs: 'flex-start', md: 'center' },
                flexShrink: 0,
                '& .MuiButton-startIcon': {
                  ml: 0,
                  mr: 0.5,
                },
                '&:hover': {
                  borderColor: '#7a9cff',
                  backgroundColor: 'rgba(63, 105, 248, 0.18)',
                },
              }}
            >
              {isCompactHeader ? 'Download' : 'Download Learning Pathway'}
            </Button>
          </Box>
        </Paper>

        {/* Section Content */}
        <Box sx={{ width: '100%' }}>
          {MANUAL_SECTIONS[selectedIndex].subs.map((sub, idx) => (
            <ManualAccordion key={`sub-${idx}`} title={sub.title} description={sub.description} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default UserManualNew;
